import { isEmpty, isEmail } from '@jjj/components';
import { keyBy } from 'src/helpers/core';
import { IField } from '@dash/editForm';
import actionsTypes from './actionTypes';
import {
  Attribute,
  IAddBankAccountState,
  IChangePropertyExtendedPayload,
  IFieldTypes,
  ILoadFormDataSuccessPayload,
  ISetupIntentBankAccountSuccessPayload,
} from './types';
import { createReducer, getError } from '../../../../../../../../../helpers/reducer';
import { t } from '../../../../../../../../../i18n';

function validate(attr: Attribute, value: IFieldTypes, error = '') {
  let errorMessage = error;

  if (attr === 'email') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.bankAccounts.email_field'),
      });
    } else if (!isEmail(value)) {
      errorMessage = t('dash.validation.valid_error', {
        fieldName: t('dash.units.unit.bankAccounts.email_field'),
      });
    }
  }

  if (attr === 'account_holder_name') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.bankAccounts.account_holder_name_field'),
      });
    }
  }

  if (attr === 'account_holder_type') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.bankAccounts.account_holder_type_field'),
      });
    }
  }

  if (attr === 'account_number') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.bankAccounts.account_number_field'),
      });
    } else if (value && value.length > 16) {
      errorMessage = t('dash.validation.max_digits_error', {
        fieldName: t('dash.units.unit.bankAccounts.account_number_field'),
        digits: 16,
      });
    }
  }

  if (attr === 'routing_number') {
    if (isEmpty(value)) {
      errorMessage = t('dash.validation.required_error', {
        fieldName: t('dash.units.unit.bankAccounts.routing_number_field'),
      });
    } else if (value && value.length !== 9) {
      errorMessage = t('dash.validation.digits_error', {
        fieldName: t('dash.units.unit.bankAccounts.account_number_title'),
        digits: 9,
      });
    }
  }

  return errorMessage;
}

const getInitialField = (fieldName: Attribute, value = null) => ({
  name: fieldName,
  value,
  touched: false,
  error: validate(fieldName, value || ''),
});

const initialEditForm = {
  account_holder_name: getInitialField('account_holder_name'),
  account_number: getInitialField('account_number'),
  account_holder_type: getInitialField('account_holder_type'),
  account_holder_number: getInitialField('account_holder_number'),
  routing_number: getInitialField('routing_number'),
  email: getInitialField('email'),
};

const initState: IAddBankAccountState = {
  isLoading: false,
  editForm: initialEditForm,
  isModalVisible: false,
  formInfo: undefined,
  isFormInfoLoading: false,
  baserErrors: undefined,
  clientSecret: '',
};

function onChangeProperty(
  state: IAddBankAccountState,
  { payload }: { payload: IChangePropertyExtendedPayload },
): IAddBankAccountState {
  const { attr, value, error } = payload;
  const { editForm } = state;

  const getValue = (val: IFieldTypes, attribute: Attribute) => ({
    value: val,
    error: validate(attribute, val, error),
    name: attribute,
    touched: true,
  });

  return {
    ...state,
    editForm: {
      ...editForm,
      [attr]: getValue(value, attr),
    },
  };
}

function onAddBankAccountRequest(state: IAddBankAccountState): IAddBankAccountState {
  return {
    ...state,
    isLoading: true,
  };
}

function onAddBankAccountSuccess(state: IAddBankAccountState): IAddBankAccountState {
  return {
    ...state,
    editForm: initialEditForm,
    isLoading: false,
  };
}

function onAddBankAccountError(
  state: IAddBankAccountState,
  { payload }: { payload: { data: { [key: string]: string | string[] } } },
): IAddBankAccountState {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    (entry: [Attribute, IField]) => {
      return entry[0];
    },
    (entry: [Attribute, IField]) => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  return {
    ...state,
    isLoading: false,
    editForm,
    baserErrors: data.base || state.baserErrors,
  };
}

function onChangeModalVisibility(state: IAddBankAccountState) {
  return {
    ...state,
    isModalVisible: !state.isModalVisible,
  };
}

function onProcessActionRequest(state: IAddBankAccountState) {
  return {
    ...state,
    isLoading: true,
  };
}

function onLoadFormInfoRequest(state: IAddBankAccountState) {
  return {
    ...state,
    isFormInfoLoading: true,
  };
}

function onLoadFormInfoSuccess(state: IAddBankAccountState, { payload }: { payload: ILoadFormDataSuccessPayload }) {
  const { data } = payload;

  return {
    ...state,
    formInfo: data,
    isFormInfoLoading: false,
  };
}

function onLoadFormInfoError(state: IAddBankAccountState) {
  return {
    ...state,
    isFormInfoLoading: false,
  };
}

function onSetupIntentBankAccount(
  state: IAddBankAccountState,
  { payload }: { payload: ISetupIntentBankAccountSuccessPayload },
) {
  const { clientSecret } = payload;

  return {
    ...state,
    clientSecret,
  };
}

export default createReducer(initState, {
  [actionsTypes.CHANGE_PROPERTY]: onChangeProperty,
  [actionsTypes.ADD_BANK_ACCOUNT_REQUEST]: onAddBankAccountRequest,
  [actionsTypes.ADD_BANK_ACCOUNT_SUCCESS]: onAddBankAccountSuccess,
  [actionsTypes.ADD_BANK_ACCOUNT_ERROR]: onAddBankAccountError,
  [actionsTypes.CHANGE_MODAL_VISIBILITY]: onChangeModalVisibility,
  [actionsTypes.PROCESS_ACTION_REQUEST]: onProcessActionRequest,
  [actionsTypes.LOAD_FORM_INFO_REQUEST]: onLoadFormInfoRequest,
  [actionsTypes.LOAD_FORM_INFO_SUCCESS]: onLoadFormInfoSuccess,
  [actionsTypes.LOAD_FORM_INFO_ERROR]: onLoadFormInfoError,
  [actionsTypes.SETUP_INTENT_BANK_ACCOUNT_SUCCESS]: onSetupIntentBankAccount,
});
